import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Stack
} from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Person,
  LocationOn,
  Facebook,
  Instagram,
  WhatsApp
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { userService } from '../../services/userService';
import { programService } from '../../services/programService';


const DJCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef(null);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const itemsPerView = isXs ? 1 : isSm ? 2 : 3;

  // Buscar todos os DJs ativos
  const { data: djsData, isLoading, error } = useQuery({
    queryKey: ['djs-active'],
    queryFn: async () => {
      try {
        const response = await userService.getActiveDJs();
        const djs = response.data?.djs || [];
        return djs;
      } catch (error) {
        console.error('Erro ao buscar DJs:', error);
        return [];
      }
    },
  });

  // Consultar qual DJ está ao vivo no ar no momento
  const { data: liveProgramData } = useQuery({
    queryKey: ['liveProgram'],
    queryFn: () => programService.getLiveProgram(),
    refetchInterval: 15000,
  });
  const liveDJId = liveProgramData?.data?.type === 'program' ? Number(liveProgramData?.data?.data?.dj?.id) : null;

  const djs = djsData || [];

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || djs.length <= itemsPerView) return;

    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        const maxIndex = Math.max(0, djs.length - itemsPerView);
        return prev >= maxIndex ? 0 : prev + itemsPerView;
      });
    }, 4000); // Change slide every 4 seconds

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, djs.length, itemsPerView]);

  // Pause auto-play when user interacts
  const pauseAutoPlay = () => {
    setIsAutoPlaying(false);
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  // Resume auto-play after 5 seconds of inactivity
  useEffect(() => {
    if (!isAutoPlaying) {
      const resumeTimer = setTimeout(() => {
        setIsAutoPlaying(true);
      }, 5000);
      return () => clearTimeout(resumeTimer);
    }
  }, [isAutoPlaying]);

  const handlePrevious = () => {
    pauseAutoPlay();
    setCurrentIndex((prev) => Math.max(0, prev - itemsPerView));
  };

  const handleNext = () => {
    pauseAutoPlay();
    setCurrentIndex((prev) => {
      const maxIndex = Math.max(0, djs.length - itemsPerView);
      return Math.min(maxIndex, prev + itemsPerView);
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress sx={{ color: '#8B5CF6' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Erro ao carregar DJs: {error.message}
      </Alert>
    );
  }

  if (djs.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Person sx={{ fontSize: 60, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
        <Typography variant="h6" sx={{ opacity: 0.7 }}>
          Nenhum DJ cadastrado
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.5 }}>
          Em breve teremos nossa equipe de DJs
        </Typography>
      </Box>
    );
  }

  const maxIndex = Math.max(0, djs.length - itemsPerView);
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < maxIndex;

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Navigation Buttons */}
      {canGoPrevious && (
        <IconButton
          onClick={handlePrevious}
          sx={{
            position: 'absolute',
            left: { xs: -8, sm: -12, md: -16 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: { xs: 32, sm: 40, md: 48 },
            height: { xs: 32, sm: 40, md: 48 },
            bgcolor: 'rgba(139, 92, 246, 0.9)',
            color: 'white',
            border: '2px solid rgba(139, 92, 246, 0.5)',
            boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
            '&:hover': {
              bgcolor: 'rgba(139, 92, 246, 1)',
              transform: 'translateY(-50%) scale(1.1)',
            },
          }}
        >
          <ChevronLeft sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }} />
        </IconButton>
      )}

      {canGoNext && (
        <IconButton
          onClick={handleNext}
          sx={{
            position: 'absolute',
            right: { xs: -8, sm: -12, md: -16 },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: { xs: 32, sm: 40, md: 48 },
            height: { xs: 32, sm: 40, md: 48 },
            bgcolor: 'rgba(139, 92, 246, 0.9)',
            color: 'white',
            border: '2px solid rgba(139, 92, 246, 0.5)',
            boxShadow: '0 4px 20px rgba(139, 92, 246, 0.3)',
            '&:hover': {
              bgcolor: 'rgba(139, 92, 246, 1)',
              transform: 'translateY(-50%) scale(1.1)',
            },
          }}
        >
          <ChevronRight sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }} />
        </IconButton>
      )}

      {/* Cards Container */}
      <Box sx={{ overflow: 'hidden', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            transform: `translateX(-${(100 / itemsPerView) * currentIndex}%)`,
            transition: 'transform 0.5s ease-in-out',
          }}
        >
          {djs.map((dj) => {
            const isLiveOnAir = liveDJId && Number(dj.id) === liveDJId;

            return (
              <Box
                key={dj.id}
                sx={{
                  flex: `0 0 ${100 / itemsPerView}%`,
                  px: { xs: 1, sm: 1.5, md: 2 },
                  minWidth: 0,
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    background: isLiveOnAir
                      ? 'linear-gradient(135deg, rgba(255, 107, 53, 0.14) 0%, rgba(10, 10, 14, 0.95) 100%)'
                      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(10, 10, 14, 0.95) 100%)',
                    border: isLiveOnAir
                      ? '2px solid #FF6B35'
                      : '1px solid rgba(255, 107, 53, 0.22)',
                    borderRadius: 3.5,
                    overflow: 'hidden',
                    transition: 'all 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                    boxShadow: isLiveOnAir
                      ? '0 12px 35px rgba(255, 107, 53, 0.3), 0 0 15px rgba(255, 107, 53, 0.2)'
                      : '0 8px 24px rgba(0, 0, 0, 0.6)',
                    '&:hover': {
                      transform: 'translateY(-7px)',
                      border: isLiveOnAir
                        ? '2px solid #FF8C5A'
                        : '1px solid rgba(0, 212, 255, 0.5)',
                      boxShadow: '0 18px 42px rgba(0, 0, 0, 0.8), 0 0 25px rgba(0, 212, 255, 0.25)',
                      '& .dj-avatar': {
                        transform: 'scale(1.06)',
                        boxShadow: '0 0 25px rgba(0, 212, 255, 0.55)',
                        borderColor: '#00D4FF',
                      },
                    },
                  }}
                >
                  {/* Top Live Badge if DJ is currently on air */}
                  {isLiveOnAir && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        bgcolor: 'rgba(255, 23, 68, 0.25)',
                        border: '1px solid #FF1744',
                        borderRadius: '20px',
                        px: 1.4,
                        py: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.8,
                        boxShadow: '0 0 14px rgba(255, 23, 68, 0.6)',
                        zIndex: 2,
                        animation: 'pulseLiveBadge 1.8s infinite',
                        '@keyframes pulseLiveBadge': {
                          '0%': { boxShadow: '0 0 0 0 rgba(255, 23, 68, 0.7)' },
                          '70%': { boxShadow: '0 0 0 8px rgba(255, 23, 68, 0)' },
                          '100%': { boxShadow: '0 0 0 0 rgba(255, 23, 68, 0)' },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#FF1744',
                        }}
                      />
                      <Typography
                        sx={{
                          color: '#FFF',
                          fontWeight: 900,
                          fontSize: '0.65rem',
                          fontFamily: 'Orbitron, sans-serif',
                          letterSpacing: '0.5px',
                        }}
                      >
                        NO AR AGORA
                      </Typography>
                    </Box>
                  )}

                  <CardContent sx={{ textAlign: 'center', px: { xs: 2, md: 3 }, py: { xs: 3.5, md: 3 } }}>
                    {/* Avatar Container */}
                    <Box sx={{ position: 'relative', display: 'inline-block', mb: 2.5, mt: isLiveOnAir ? 1.5 : 0.5 }}>
                      <Avatar
                        src={dj.profile_image}
                        className="dj-avatar"
                        imgProps={{ loading: 'lazy', decoding: 'async' }}
                        sx={{
                          width: { xs: 130, sm: 120, md: 140 },
                          height: { xs: 130, sm: 120, md: 140 },
                          mx: 'auto',
                          border: isLiveOnAir ? '4px solid #FF6B35' : '4px solid #8B5CF6',
                          boxShadow: isLiveOnAir
                            ? '0 0 22px rgba(255, 107, 53, 0.6)'
                            : { xs: '0 0 12px rgba(139, 92, 246, 0.4)', md: '0 0 25px rgba(139, 92, 246, 0.6)' },
                          transition: 'all 0.35s ease',
                        }}
                      >
                        <Person sx={{ fontSize: { xs: 60, sm: 50, md: 60 } }} />
                      </Avatar>

                      {/* Decorative Ring - Desktop only to save mobile GPU cycles */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -8,
                          left: -8,
                          right: -8,
                          bottom: -8,
                          borderRadius: '50%',
                          border: isLiveOnAir ? '2px solid rgba(255, 107, 53, 0.4)' : '2px solid rgba(6, 182, 212, 0.3)',
                          animation: 'spin 10s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                          display: { xs: 'none', md: 'block' },
                        }}
                      />
                    </Box>

                    {/* Name */}
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        mb: 0.8,
                        color: 'white',
                        fontFamily: 'Orbitron, sans-serif',
                        textShadow: '0 2px 10px rgba(0, 0, 0, 0.7)',
                        fontSize: { xs: '1.25rem', sm: '1.2rem', md: '1.35rem' },
                        letterSpacing: '0.5px',
                      }}
                    >
                      {dj.name}
                    </Typography>

                    {/* Location */}
                    {dj.city && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          bgcolor: 'rgba(0, 212, 255, 0.1)',
                          py: 0.4,
                          px: 1.8,
                          borderRadius: 10,
                          width: 'fit-content',
                          mx: 'auto',
                          border: '1px solid rgba(0, 212, 255, 0.25)',
                        }}
                      >
                        <LocationOn sx={{ fontSize: 16, color: '#00D4FF', mr: 0.5 }} />
                        <Typography variant="body2" sx={{ color: '#00D4FF', fontWeight: 600, fontSize: '0.8rem' }}>
                          {dj.city && dj.state ? `${dj.city} - ${dj.state}` : dj.city}
                        </Typography>
                      </Box>
                    )}

                    {/* Social Links */}
                    <Stack
                      direction="row"
                      spacing={1.5}
                      justifyContent="center"
                      sx={{ mt: 1 }}
                    >
                      {dj.facebook && (
                        <IconButton
                          size="small"
                          onClick={() => window.open(dj.facebook, '_blank')}
                          sx={{
                            color: 'white',
                            bgcolor: 'rgba(24, 119, 242, 0.18)',
                            border: '1px solid rgba(24, 119, 242, 0.4)',
                            borderRadius: '10px',
                            p: 0.9,
                            '&:hover': {
                              bgcolor: '#1877F2',
                              transform: 'translateY(-3px)',
                              boxShadow: '0 6px 16px rgba(24, 119, 242, 0.5)',
                            },
                            transition: 'all 0.25s ease',
                          }}
                        >
                          <Facebook sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}

                      {dj.instagram && (
                        <IconButton
                          size="small"
                          onClick={() => window.open(dj.instagram, '_blank')}
                          sx={{
                            color: 'white',
                            bgcolor: 'rgba(228, 64, 95, 0.18)',
                            border: '1px solid rgba(228, 64, 95, 0.4)',
                            borderRadius: '10px',
                            p: 0.9,
                            '&:hover': {
                              background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                              transform: 'translateY(-3px)',
                              boxShadow: '0 6px 16px rgba(228, 64, 95, 0.5)',
                            },
                            transition: 'all 0.25s ease',
                          }}
                        >
                          <Instagram sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}

                      {dj.whatsapp && (
                        <IconButton
                          size="small"
                          onClick={() => window.open(`https://wa.me/${dj.whatsapp.replace(/\D/g, '')}`, '_blank')}
                          sx={{
                            color: 'white',
                            bgcolor: 'rgba(37, 211, 102, 0.18)',
                            border: '1px solid rgba(37, 211, 102, 0.4)',
                            borderRadius: '10px',
                            p: 0.9,
                            '&:hover': {
                              bgcolor: '#25D366',
                              transform: 'translateY(-3px)',
                              boxShadow: '0 6px 16px rgba(37, 211, 102, 0.5)',
                            },
                            transition: 'all 0.25s ease',
                          }}
                        >
                          <WhatsApp sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Dots Indicator */}
      {djs.length > itemsPerView && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 1 }}>
          {Array.from({ length: Math.ceil(djs.length / itemsPerView) }).map((_, index) => (
            <Box
              key={index}
              onClick={() => {
                pauseAutoPlay();
                setCurrentIndex(index * itemsPerView);
              }}
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: currentIndex === index * itemsPerView ? '#8B5CF6' : 'rgba(255, 255, 255, 0.3)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: currentIndex === index * itemsPerView ? '#8B5CF6' : 'rgba(255, 255, 255, 0.5)',
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DJCarousel;